/* BEZPEČNÉ HODNOTY — jedno místo na obranu proti datům jiného TVARU.
 *
 * PROČ (živý průchod CRM, 17. 8. 2026):
 * Po celé appce se opakoval zápis `(x || []).reduce(…)` a `(x || "").replace(…)`.
 * Vypadá jako pojistka, ale chrání POUZE proti `null` a `undefined`. Jakmile na
 * to místo dorazí jiný TYP — objekt místo pole, číslo místo textu, `{}` z rozbité
 * odpovědi API, `{length: 3}`, které se jako pole jen tváří — `.reduce`/`.replace`
 * na té hodnotě neexistuje, vyletí výjimka a spadne CELÁ obrazovka.
 * U prezentace plánu to není „chyba v UI": běží promítaná klientovi na plátno.
 *
 * ⚠️ PRAVIDLO, KTERÉ TENHLE SOUBOR DRŽÍ: obrana NENÍ tiché spolknutí chyby.
 * `asArr({})` vrátí `[]`, aby se nespadlo — ale `jePoleRozbite({})` řekne `true`,
 * aby obrazovka mohla přiznat „data se nenačetla" místo toho, aby ukázala 0 Kč.
 * Falešná nula je horší než hlášená chyba: vypadá jako údaj a poradce podle ní
 * radí. Proto k oběma převodníkům patří i jejich detektor a sběrač `sberProblemu`.
 *
 * ROZLIŠENÍ, na kterém všechno stojí:
 *   • `null` / `undefined` / `[]` / `""`  = PRÁZDNO. Legitimní stav, nikdo nic
 *     nevyplnil. Žádný poplach.
 *   • cokoli jiného, než se čekalo       = ROZBITO. Hodnota tam JE, ale nejde
 *     použít → převede se na prázdno A OHLÁSÍ SE.
 *
 * Vzor `asArr` (pole objektů, ostatní se zahodí) se v repu už používal
 * v `components/notif/notifLists.js` a v osmi kopiích po `components/crm/**`
 * a `lib/crm/**`. Tady má konečně jedno bydliště; kopie se na něj postupně
 * přepínají, ať se chování nerozjede.
 */

/** Pole objektů. Cokoli jiného (i `{length: 3}`) → `[]`. Nikdy nespadne. */
export function asArr(v) {
  return Array.isArray(v) ? v.filter((x) => x && typeof x === "object") : [];
}

/** Text. Primitivum se přepíše na text, objekt/pole/NaN → `""`. Nikdy nespadne. */
export function asText(v) {
  if (typeof v === "string") return v;
  if (typeof v === "number") return Number.isFinite(v) ? String(v) : "";
  if (typeof v === "boolean") return String(v);
  return "";
}

/* ===== Seznamy skládané z toho, co vyplnil člověk =====
 *
 * PROČ (vizuální QA 20. 8. 2026, nález N8): pole „Co pro vás znamenají peníze"
 * je textarea. Poradce v ní nechá prázdné řádky — a `replace(/\n/g, ", ")`
 * z nich udělal osiřelé čárky. Na plátně pak stálo obřím písmem:
 *
 *     „svoboda nezavislost, , ,“
 *
 * Vzorec split+filter+join byl na dvou místech zkopírovaný. Tady má jedno
 * bydliště, ať se ta třetí kopie nenapíše znovu špatně. */

/** Neprázdné řádky textarey, ořezané. Cokoli jiného než text → `[]`. */
export function radkyTextu(v) {
  return asText(v).split(/\r?\n+/).map((s) => s.trim()).filter(Boolean);
}

/**
 * Řádky textarey jako jedna věta („svoboda, nezávislost").
 * Prázdný vstup → `""` — a volající PAK NESMÍ vykreslit ani nadpis.
 * Nadpis „Vaše hodnoty" nad prázdnem je horší než nic: tváří se jako
 * vyplněný údaj.
 */
export function seznamZTextu(v, oddelovac = ", ") {
  return radkyTextu(v).join(oddelovac);
}

/** Objekt na čtení klíčů. Pole i primitivum → `{}`. Nikdy nespadne. */
export function asObj(v) {
  return v && typeof v === "object" && !Array.isArray(v) ? v : {};
}

/** Konečné číslo, jinak `null` — schválně NE nula, tu si dosadí až volající. */
export function asCislo(v) {
  if (v === "" || v === null || v === undefined || typeof v === "boolean") return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
}

/* ===== Detektory: dorazil nesmysl, nebo prostě nic? ===== */

/** `true` = na místě pole je něco, co se použít nedá (a nese to hodnotu). */
export function jePoleRozbite(v) {
  if (v === null || v === undefined) return false;  // nic nepřišlo = prázdno
  if (!Array.isArray(v)) return true;               // objekt/číslo/text místo pole
  /* Díry po smazaných řádcích (`null` položky) se zahodí bez poplachu —
   * nic se tím neztrácí. Skalár uvnitř pole ale hodnotu NESE (`[12345]` místo
   * `[{jistina: 12345}]`), a ta by tichým filtrem zmizela z výpočtu. */
  return v.some((x) => x !== null && x !== undefined && typeof x !== "object");
}

/** `true` = na místě textu je objekt/pole/NaN — `.replace`/`.split` by spadly. */
export function jeTextRozbity(v) {
  if (v === null || v === undefined || v === "") return false;
  if (typeof v === "string") return false;
  if (typeof v === "number") return !Number.isFinite(v);
  return typeof v !== "boolean";
}

/** `true` = na místě objektu je pole nebo primitivum. */
export function jeObjektRozbity(v) {
  if (v === null || v === undefined) return false;
  return typeof v !== "object" || Array.isArray(v);
}

/* ===== Sběrač: převeď a zároveň si zapiš, co bylo rozbité ===== */

/**
 * Vrátí převodníky, které si vedle bezpečné hodnoty poznamenají CESTU k poli,
 * jež dorazilo v jiném tvaru. Engine pak seznam vydá ven (`dataProblemy`)
 * a obrazovka místo falešné nuly ukáže přiznaný prázdný stav.
 *
 *   const sber = sberProblemu();
 *   const dluhy = soucet(sber.pole("zavazkyB", d.zavazkyB), "jistina");
 *   return { dluhy, dataProblemy: sber.problemy };
 */
export function sberProblemu() {
  const problemy = [];
  const zapis = (cesta) => { if (cesta && !problemy.includes(cesta)) problemy.push(cesta); };
  return {
    problemy,
    /** Pole objektů + poznámka, když místo pole dorazilo něco jiného. */
    pole(cesta, v) { if (jePoleRozbite(v)) zapis(cesta); return asArr(v); },
    /** Text + poznámka, když místo textu dorazil objekt/pole. */
    text(cesta, v) { if (jeTextRozbity(v)) zapis(cesta); return asText(v); },
    /** Objekt + poznámka, když místo objektu dorazilo pole/primitivum. */
    obj(cesta, v) { if (jeObjektRozbity(v)) zapis(cesta); return asObj(v); },
    /** Přebere problémy z vnořeného enginu, s předponou (`solver.cile`). */
    prevezmi(predpona, seznam) {
      asArr0(seznam).forEach((p) => zapis(predpona ? `${predpona}.${p}` : p));
    },
  };
}

/* Pomocná varianta `asArr` pro seznam TEXTŮ (cest), ne objektů. */
function asArr0(v) {
  return Array.isArray(v) ? v.filter((x) => typeof x === "string" && x) : [];
}

/** Seznam cest → jedna věta pro klienta/poradce. Prázdný seznam → `""`. */
export function popisProblemu(problemy) {
  const list = asArr0(problemy);
  return list.length ? list.join(", ") : "";
}

export default asArr;
